import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  apiGet,
  apiPost,
  type PublishResponse,
  type SignalExtractionPayload,
  type SignalCandidateList,
} from "@/lib/api";

export interface SignalCandidatesQueryParams {
  source_ids?: string[];
  item_types?: string[];
  symbol?: string;
  since?: string;
  until?: string;
  time_basis?: "captured" | "source";
  include_hidden?: boolean;
  limit?: number;
  correlation_window?: string;
}

async function fetchSignalCandidates(p: SignalCandidatesQueryParams): Promise<SignalCandidateList> {
  return apiGet<SignalCandidateList>("/api/v1/signal-candidates", {
    source_id: p.source_ids,
    item_type: p.item_types,
    symbol: p.symbol,
    since: p.since,
    until: p.until,
    time_basis: p.time_basis,
    include_hidden: p.include_hidden,
    limit: p.limit,
    correlation_window: p.correlation_window,
  });
}

export function useSignalCandidates(params: SignalCandidatesQueryParams) {
  return useQuery<SignalCandidateList>({
    queryKey: ["signal-candidates", params],
    queryFn: () => fetchSignalCandidates(params),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });
}

export function useAnalyzeSignalCandidate() {
  const qc = useQueryClient();
  return useMutation<
    SignalExtractionPayload,
    Error,
    { inbox_item_ids: string[] },
    string | number
  >({
    mutationFn: (vars) =>
      apiPost<SignalExtractionPayload>("/api/v1/signal-candidates/analyze", vars),
    onMutate: (vars) =>
      toast.loading(
        `Analyzing ${vars.inbox_item_ids.length} related inbox message${vars.inbox_item_ids.length === 1 ? "" : "s"}…`,
      ),
    onSuccess: (payload) => {
      const count = payload.inbox_item_ids.length;
      toast.success(`Analyzed ${count} inbox message${count === 1 ? "" : "s"}`);
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["signal-candidates"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
    onError: (err) => {
      toast.error(`Candidate analysis failed: ${err.message}`);
    },
    onSettled: (_data, _error, _vars, toastId) => {
      if (toastId !== undefined) toast.dismiss(toastId);
    },
  });
}

export function usePublishSignalCandidates() {
  const qc = useQueryClient();
  return useMutation<
    PublishResponse,
    Error,
    {
      targets: string[];
      candidates: Array<{
        candidate_id?: string | null;
        symbol: string;
        inbox_item_ids: string[];
        effective_dsl_text?: string | null;
      }>;
      mode?: "prepare_draft";
    },
    string | number
  >({
    mutationFn: (vars) =>
      apiPost<PublishResponse>("/api/v1/publish", {
        ...vars,
        mode: vars.mode ?? "prepare_draft",
      }),
    onMutate: (vars) => toast.loading(`Publishing ${vars.candidates.length} candidate${vars.candidates.length === 1 ? "" : "s"}...`),
    onSuccess: (payload) => {
      const unavailable = payload.results.filter((result) => !result.target_available);
      const ready = payload.results.filter((result) => result.target_available);
      qc.invalidateQueries({ queryKey: ["outbox"] });
      qc.invalidateQueries({ queryKey: ["publish-targets"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
      if (unavailable.length > 0 && ready.length === 0) {
        toast.warning(`Queued ${unavailable.length} publish request${unavailable.length === 1 ? "" : "s"}`);
        return;
      }
      toast.success(`Published ${payload.results.length} request${payload.results.length === 1 ? "" : "s"}`);
    },
    onError: (err) => {
      toast.error(`Publish failed: ${err.message}`);
    },
    onSettled: (_data, _error, _vars, toastId) => {
      if (toastId !== undefined) toast.dismiss(toastId);
    },
  });
}
